/**
 * The right-hand pane: one venue's photo, metadata and sourcing.
 *
 * The mobile equivalent is `app/src/components/VenueAdmin.tsx`'s
 * `VenueEditor` — same rule (`hero_key` never in the browser's own PATCH),
 * different shell. Not shared: that file is a React
 * component, and reusing it here would need the `ds/` design system and
 * `Sheet`'s drag gesture along with it, none of which belongs in a desktop
 * panel. What *is* shared is the logic that is not presentational —
 * `sourcing.ts` and `venueSourcing.ts`, imported straight from `app/src/lib`
 * a few lines below.
 */
import {
  useEffect, useRef, useState,
  type ClipboardEvent as ReactClipboardEvent, type DragEvent as ReactDragEvent,
} from 'react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { sourcingSentence, type SourceRecipe } from '../../app/src/lib/sourcing'
import { fetchVenueSourcing } from '../../app/src/lib/venueSourcing'
import { imageFromDrop } from '../../app/src/lib/dragImage'
import { fetchOrganisation, fetchUpcomingEvents, type OrganisationInfo, type VenueEvent } from '../../app/src/lib/venueRecord'
import { fetchVenueNotes, type VenueNote } from '../../app/src/lib/venueNotes'
import { addNote, setNoteDone, type NoteKind } from '../../app/src/lib/db'
import { EventsTable } from './EventsTable'
import { KINDS } from './venueGroups'
import type { Venue } from './App'

/** `MAX_IMAGE_BYTES` in `src/assets.ts`, restated — see the identical note
 *  in `app/src/components/VenueAdmin.tsx`. The Worker enforces the real
 *  ceiling; this only saves a doomed upload's round trip. */
const MAX_IMAGE_BYTES = 8 * 1024 * 1024

const ASSET_BASE = (import.meta.env.VITE_ASSET_BASE_URL as string | undefined)?.replace(/\/+$/, '')
const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')
const heroSrc = (key: string | null) => (key && ASSET_BASE ? `${ASSET_BASE}/${key}` : null)

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

/** A readable line for street/postcode/district/country, skipping whatever
 *  the row does not have rather than printing a comma for it. */
function addressLine(v: Venue): string | null {
  const cityLine = [v.postcode, v.city].filter(Boolean).join(' ')
  const parts = [v.street, cityLine || null, v.district ? `(${v.district})` : null, v.country]
    .filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

const NOTE_KIND_LABEL: Record<NoteKind, string> = { fix: 'Broken', change: 'Change', idea: 'Idea' }

function useObjectUrl() {
  const [blob, setBlob] = useState<Blob | null>(null)
  const url = useRef<string | null>(null)
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => () => { if (url.current) URL.revokeObjectURL(url.current) }, [])

  function set(next: Blob | null) {
    if (url.current) URL.revokeObjectURL(url.current)
    url.current = next ? URL.createObjectURL(next) : null
    setSrc(url.current)
    setBlob(next)
  }

  return { blob, src, set }
}

export function VenuePanel({ supabase, venue, onSaved }: {
  supabase: SupabaseClient
  venue: Venue
  onSaved: (v: Venue) => void
}) {
  const [name, setName] = useState(venue.name)
  const [kind, setKind] = useState(venue.kind ?? 'other')
  const [city, setCity] = useState(venue.city ?? '')
  const [siteUrl, setSiteUrl] = useState(venue.site_url ?? '')
  const [commercial, setCommercial] = useState(!!venue.commercial)
  const [credit, setCredit] = useState(venue.hero_credit ?? '')
  const [license, setLicense] = useState(venue.hero_license ?? '')
  const [alt, setAlt] = useState(venue.hero_alt ?? '')

  const pending = useObjectUrl()
  const [pasteError, setPasteError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // The photo saves itself the moment it is accepted — Doug's ask, so
  // picking one is the whole action and not the first half of one that
  // still needs a click. `savedHeroKey` is a local override of
  // `venue.hero_key` for exactly the gap between that upload finishing and
  // the parent's `onSaved` refetch landing a new `venue` prop with the same
  // key in it — without it the preview would flash back to the old photo
  // (or none) for a moment once `pending` clears.
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [photoSaved, setPhotoSaved] = useState(false)
  const [savedHeroKey, setSavedHeroKey] = useState<string | null>(null)

  const [sourcing, setSourcing] = useState<SourceRecipe[] | null>(null)
  const [sourcingError, setSourcingError] = useState<string | null>(null)

  const [org, setOrg] = useState<OrganisationInfo | null>(null)
  const [orgError, setOrgError] = useState<string | null>(null)
  const [events, setEvents] = useState<VenueEvent[] | null>(null)
  const [eventsError, setEventsError] = useState<string | null>(null)

  const [notes, setNotes] = useState<VenueNote[] | null>(null)
  const [notesError, setNotesError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [noteBody, setNoteBody] = useState('')
  const [noteKind, setNoteKind] = useState<NoteKind>('fix')
  const [noteBusy, setNoteBusy] = useState(false)
  const [noteError, setNoteError] = useState<string | null>(null)
  const [showDoneNotes, setShowDoneNotes] = useState(false)

  async function refetchNotes() {
    try {
      setNotes(await fetchVenueNotes(venue.id))
      setNotesError(null)
    } catch (e) {
      setNotesError(e instanceof Error ? e.message : String(e))
    }
  }

  // Re-seed every field when a different venue is selected — this component
  // stays mounted across selections (it is one pane, not a route), so
  // without this the form would keep showing the previous venue's draft.
  useEffect(() => {
    setName(venue.name)
    setKind(venue.kind ?? 'other')
    setCity(venue.city ?? '')
    setSiteUrl(venue.site_url ?? '')
    setCommercial(!!venue.commercial)
    setCredit(venue.hero_credit ?? '')
    setLicense(venue.hero_license ?? '')
    setAlt(venue.hero_alt ?? '')
    pending.set(null)
    setPasteError(null)
    setSaveError(null)
    setSaved(false)
    setPhotoBusy(false)
    setPhotoError(null)
    setPhotoSaved(false)
    setSavedHeroKey(null)
    setSourcing(null)
    setSourcingError(null)
    setOrg(null)
    setOrgError(null)
    setEvents(null)
    setEventsError(null)
    setNotes(null)
    setNotesError(null)
    setNoteBody('')
    setNoteError(null)
    setShowDoneNotes(false)

    let live = true
    fetchVenueSourcing(venue.id)
      .then(rows => { if (live) setSourcing(rows) })
      .catch(e => { if (live) setSourcingError(e instanceof Error ? e.message : String(e)) })
    fetchOrganisation(venue.organisation_id)
      .then(o => { if (live) setOrg(o) })
      .catch(e => { if (live) setOrgError(e instanceof Error ? e.message : String(e)) })
    // Not the default 15 — "Coming up" is a full table on this screen, not
    // a phone card's short list, and a venue with a busy recipe (a KULTURpur
    // stage, an index-berlin gallery) can genuinely have more than that on
    // the books at once. 300 is a ceiling against a runaway query, not a
    // number Doug should ever actually hit.
    fetchUpcomingEvents(venue.id, 300)
      .then(rows => { if (live) setEvents(rows) })
      .catch(e => { if (live) setEventsError(e instanceof Error ? e.message : String(e)) })
    fetchVenueNotes(venue.id)
      .then(rows => { if (live) setNotes(rows) })
      .catch(e => { if (live) setNotesError(e instanceof Error ? e.message : String(e)) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue.id])

  async function submitNote() {
    if (!noteBody.trim()) return
    setNoteBusy(true)
    setNoteError(null)
    const { error } = await addNote(noteBody, noteKind, venue.id)
    setNoteBusy(false)
    if (error) { setNoteError(error.message); return }
    setNoteBody('')
    void refetchNotes()
  }

  async function tickNote(id: string) {
    await setNoteDone(id, true)
    void refetchNotes()
  }

  function acceptImage(file: File | null) {
    if (!file) return
    if (!file.type.startsWith('image/')) { setPasteError('That is not an image.'); return }
    if (file.size > MAX_IMAGE_BYTES) {
      setPasteError(`That image is ${Math.round(file.size / 1024 / 1024)}MB; the ceiling is 8MB.`)
      return
    }
    setPasteError(null)
    pending.set(file)
    void uploadPhoto(file)
  }

  /** Uploads immediately on acceptance — see the note by `savedHeroKey`
   *  above. On failure `pending` is left exactly as it was, so the picked
   *  photo is still on screen to retry rather than lost the moment the
   *  network hiccups. */
  async function uploadPhoto(file: Blob) {
    setPhotoBusy(true)
    setPhotoError(null)
    setPhotoSaved(false)
    try {
      if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not signed in')

      const res = await fetch(`${WORKER_URL}/venue-hero?venue=${encodeURIComponent(venue.id)}`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': file.type },
        body: file,
      })
      if (!res.ok) throw new Error(`Upload failed: ${res.status} ${await res.text()}`)
      const { hero_key } = await res.json() as { hero_key: string }

      pending.set(null)
      setSavedHeroKey(hero_key)
      setPhotoSaved(true)
      onSaved({ ...venue, hero_key })
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : String(e))
    } finally {
      setPhotoBusy(false)
    }
  }

  function handlePaste(e: ReactClipboardEvent<HTMLDivElement>) {
    const item = [...e.clipboardData.items].find(i => i.type.startsWith('image/'))
    if (!item) return
    e.preventDefault()
    acceptImage(item.getAsFile())
  }

  // The zone accepts a drop whether or not it is already showing a photo —
  // `acceptImage` just replaces whatever `pending` holds, and the existing
  // preview (pasted or saved) has no drag handlers of its own to intercept
  // the event, so a drop over it bubbles to the same place a drop over the
  // empty hint would land.
  function handleDragOver(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault() // required for the element to be a valid drop target
    setDragOver(true)
  }

  // `dt` is read synchronously, before `imageFromDrop`'s only `await` — the
  // drag data store is only readable for the duration of this event, and
  // that is what keeps the read inside the window that still works. See
  // dragImage.ts: a drag of an <img> off a web page, rather than a file off
  // the Finder, often carries no File at all — only the URL the image was
  // rendered from, which is the case this covers.
  function handleDrop(e: ReactDragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    const dt = e.dataTransfer
    void imageFromDrop(dt).then(result => {
      if ('error' in result) setPasteError(result.error)
      else acceptImage(result.file)
    })
  }

  const canSave = !!name.trim() && !busy

  // The photo is not this function's job any more — `uploadPhoto` above
  // saves itself the moment it is picked. This is metadata only:
  // name/kind/city/website/commercial/credit/license/alt, and `hero_key` is
  // never sent here — see the same note in VenueAdmin.tsx.
  async function save() {
    setBusy(true)
    setSaveError(null)
    try {
      const { error } = await supabase
        .from('venues')
        .update({
          name: name.trim(),
          kind,
          city: city.trim() || null,
          site_url: siteUrl.trim() || null,
          commercial,
          hero_credit: credit.trim() || null,
          hero_license: license.trim() || null,
          hero_alt: alt.trim() || null,
        })
        .eq('id', venue.id)
      if (error) throw error

      setSaved(true)
      onSaved({
        ...venue,
        name: name.trim(), kind, city: city.trim() || null, site_url: siteUrl.trim() || null, commercial,
        hero_credit: credit.trim() || null, hero_license: license.trim() || null,
        hero_alt: alt.trim() || null,
      })
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const preview = pending.src ?? heroSrc(savedHeroKey ?? venue.hero_key)

  // No `useEffect` cleanup needed for the timeout: `key={selected.id}` on
  // this component in App.tsx already unmounts it on every venue switch,
  // which is what would otherwise leave a stale timer setting `copied` on a
  // component that has moved on to a different venue.
  async function copyName() {
    await navigator.clipboard.writeText(venue.name)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h1>{venue.name}</h1>
        <button className="copybtn" aria-label={`Copy "${venue.name}"`} onClick={() => void copyName()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        <span className="dim mono">{venue.id}</span>
      </div>

      <div className="panel-body">
        <section className="panel-col">
          <h2>Photo</h2>
          <div
            tabIndex={0}
            className={`hero-drop${dragOver ? ' is-dragover' : ''}`}
            onPaste={handlePaste}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            aria-label="Drag an image here, or click and paste with Cmd-V"
          >
            {preview
              ? <img src={preview} alt={alt || venue.name} />
              : <p className="dim">Drag a photo here, or click and Cmd-V.</p>}
            {/* Drawn over the existing preview too — replacing a photo this
                way is the same gesture as setting one for the first time. */}
            {dragOver && <div className="hero-drop__overlay">{preview ? 'Drop to replace' : 'Drop here'}</div>}
            {photoBusy && <div className="hero-drop__overlay">Saving…</div>}
          </div>
          <label className="filebtn">
            <input type="file" accept="image/*"
                   onChange={e => acceptImage(e.currentTarget.files?.[0] ?? null)} />
            Choose a file…
          </label>
          {pasteError && <p className="err">{pasteError}</p>}
          {photoError && (
            <p className="err">
              {photoError}{' '}
              {pending.blob && (
                <button className="btn btn-quiet" style={{ padding: '0 4px' }}
                        onClick={() => void uploadPhoto(pending.blob!)}>
                  Retry
                </button>
              )}
            </p>
          )}
          {photoSaved && !photoError && <p className="ok">Photo saved.</p>}

          <h3>Attribution</h3>
          <label>Credit
            <input value={credit} placeholder="Photographer or source"
                   onChange={e => setCredit(e.currentTarget.value)} />
          </label>
          <label>Licence
            <input value={license} placeholder="CC BY-SA 4.0"
                   onChange={e => setLicense(e.currentTarget.value)} />
          </label>
          <label>Alt text
            <input value={alt} placeholder={venue.name}
                   onChange={e => setAlt(e.currentTarget.value)} />
          </label>
        </section>

        <section className="panel-col">
          <h2>Details</h2>
          <label>Name
            <input value={name} onChange={e => setName(e.currentTarget.value)} />
          </label>
          <label>Kind
            <select value={kind} onChange={e => setKind(e.target.value)}>
              {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label>City
            <input value={city} onChange={e => setCity(e.currentTarget.value)} />
          </label>
          <label>Website
            <input value={siteUrl} placeholder="https://…" onChange={e => setSiteUrl(e.currentTarget.value)} />
          </label>
          <label className="row">
            <input type="checkbox" checked={commercial}
                   onChange={e => setCommercial(e.currentTarget.checked)} />
            Commercial — a dealer, not a place to visit
          </label>

          <h2>Where this comes from</h2>
          {sourcingError && <p className="err">{sourcingError}</p>}
          {!sourcingError && (
            <p className="dim">{sourcing ? sourcingSentence(sourcing) : 'Reading recipes and harvest runs…'}</p>
          )}
        </section>

        {/* Everything else the database holds on this row — read-only.
            Doug's ask, verbatim: "I want to see any data we have in the
            database per venue." Nothing here is editable; a column that
            needs changing belongs in the sections above, not a third place
            for the same fact to drift from. */}
        <section className="panel-col">
          <h2>Record</h2>
          <dl className="kv">
            <dt>Address</dt>
            <dd>{addressLine(venue) ?? <span className="dim">Not recorded</span>}</dd>

            <dt>Coordinates</dt>
            <dd>
              {venue.lat != null && venue.lng != null
                ? (
                  <a href={`https://www.google.com/maps?q=${venue.lat},${venue.lng}`}
                     target="_blank" rel="noopener noreferrer">
                    {venue.lat.toFixed(5)}, {venue.lng.toFixed(5)}
                  </a>
                )
                : <span className="dim">Not recorded</span>}
            </dd>

            <dt>Newsletter</dt>
            <dd>
              {orgError && <span className="err">{orgError}</span>}
              {!orgError && !venue.organisation_id && <span className="dim">No organiser on this row.</span>}
              {!orgError && venue.organisation_id && !org && <span className="dim">Reading…</span>}
              {!orgError && org && (
                org.mailSources.length
                  ? <span>Yes — via {org.mailSources.join(', ')}</span>
                  : <span className="dim">No newsletter signed up for {org.name}.</span>
              )}
            </dd>

            {org?.website && (
              <>
                <dt>Organiser site</dt>
                <dd><a href={org.website} target="_blank" rel="noopener noreferrer">{org.website}</a></dd>
              </>
            )}

            <dt>Specialism</dt>
            <dd>{venue.specialism ?? <span className="dim">Not looked at yet</span>}</dd>

            <dt>Publishes</dt>
            <dd>{venue.publishes ?? <span className="dim">unknown</span>}</dd>

            {venue.source_notes && (
              <>
                <dt>Source notes</dt>
                <dd>{venue.source_notes}</dd>
              </>
            )}

            <dt>Added</dt>
            <dd>{fmtDate(venue.created_at)}</dd>
          </dl>
        </section>

        {/* Full width, not one column of the two-up grid above — the same
            per-type Details table the global Events tab draws (see
            `eventFields.ts`), just pre-filtered to this venue and with the
            Venue column dropped, since that's the page Doug is already on. */}
        <section className="panel-col panel-col--wide">
          <h2>Coming up</h2>
          {eventsError && <p className="err">{eventsError}</p>}
          {!eventsError && !events && <p className="dim">Reading the feed…</p>}
          {!eventsError && events && events.length === 0 && <p className="dim">Nothing coming up.</p>}
          {!eventsError && events && events.length > 0 && <EventsTable events={events} showVenue={false} />}
        </section>

        {/* One note can be about one venue — Doug's clarification on the
            note field: not a general feedback box, this one (`app_notes`,
            migration 20260922110000), scoped to the venue on screen. Same
            queue, same triage query CLAUDE.md already reads at the start of
            a session; nothing moves anywhere once a note is closed, because
            "open" and "done" are the same table with `done_at` set or not. */}
        <section className="panel-col panel-col--wide">
          <h2>Notes for later</h2>
          <div className="notes-chatbar">
            <textarea
              rows={1}
              placeholder="Something to fix or check about this venue…"
              value={noteBody}
              onChange={e => setNoteBody(e.currentTarget.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submitNote() }
              }}
            />
            <select value={noteKind} onChange={e => setNoteKind(e.target.value as NoteKind)} aria-label="Kind">
              {(Object.keys(NOTE_KIND_LABEL) as NoteKind[]).map(k => (
                <option key={k} value={k}>{NOTE_KIND_LABEL[k]}</option>
              ))}
            </select>
            <button className="btn btn-primary" disabled={noteBusy || !noteBody.trim()}
                    onClick={() => void submitNote()}>
              Add
            </button>
          </div>
          {noteError && <p className="err">{noteError}</p>}
          {notesError && <p className="err">{notesError}</p>}

          {(() => {
            const open = (notes ?? []).filter(n => !n.done_at)
            const done = (notes ?? []).filter(n => n.done_at)
            return (
              <>
                {notes && open.length === 0 && <p className="dim">Nothing outstanding for this venue.</p>}
                <ul className="notelist">
                  {open.map(n => (
                    <li key={n.id}>
                      <button className="notetick" aria-label="Mark done" onClick={() => void tickNote(n.id)} />
                      <span className="tag">{NOTE_KIND_LABEL[n.kind as NoteKind] ?? n.kind}</span>
                      <span>{n.body}</span>
                    </li>
                  ))}
                </ul>
                {done.length > 0 && (
                  <>
                    <button className="btn btn-quiet" style={{ alignSelf: 'flex-start', padding: '2px 0' }}
                            onClick={() => setShowDoneNotes(v => !v)}>
                      {showDoneNotes ? 'Hide done' : `${done.length} done`}
                    </button>
                    {showDoneNotes && (
                      <ul className="notelist is-done">
                        {done.map(n => (
                          <li key={n.id}>
                            <span>{n.body}{n.resolution && <span className="dim"> — {n.resolution}</span>}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </>
            )
          })()}
        </section>
      </div>

      <div className="panel-foot">
        <button className="btn btn-primary" disabled={!canSave} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {saveError && <span className="err">{saveError}</span>}
        {saved && !saveError && <span className="ok">Saved.</span>}
      </div>
    </div>
  )
}
